import jwt from "jsonwebtoken";
import { createHash, randomBytes } from "crypto";
import { logger } from "../utils/logger";

export interface JwtConfig {
    secret: string;
    accessExpiresIn?: string;
    refreshExpiresIn?: string;
}

export interface AccessTokenPayload {
    userId: string;
    roles: string[];
    uid?: string;
    /** Authentication Assurance Level: aal1 = password/oauth, aal2 = MFA verified */
    aal?: "aal1" | "aal2";
    /** Email claim from the JWT, if present */
    email?: string;
    /** Display name claim from the JWT, if present */
    displayName?: string;
    /** Photo URL claim from the JWT, if present */
    photoURL?: string;
    /** Whether MFA has been verified for this session */
    mfa_verified?: boolean;
    /** Authentication Methods Reference — list of methods used (e.g. 'pwd', 'otp') */
    amr?: string[];
}

let jwtConfig: JwtConfig = {
    secret: "",
    accessExpiresIn: "1h",
    refreshExpiresIn: "30d"
};

/**
 * Configure JWT settings - call this during initialization.
 * Validates the secret strength to prevent deployment with default/weak secrets.
 */
export function configureJwt(config: JwtConfig): void {
    // Reject obviously weak/default secrets
    const weakSecrets = new Set([
        "secret",
        "jwt-secret",
        "jwt_secret",
        "your-secret",
        "your-super-secret-jwt-key-change-in-production",
        "super-secret-jwt-key-change-in-production",
        "change-me",
        "changeme",
        "password",
        "test",
        "mysecret",
        "my-secret",
        "my_secret",
        "example-secret",
        "please-change-me",
        "replace-this-with-a-real-secret",
        "default-secret",
        "rebase_saas_jwt_secret_must_be_long_long_long_long",
        "rebase_saas_service_key_must_be_long_long_long_long"
    ]);

    if (!config.secret || config.secret.length < 32) {
        throw new Error(
            "JWT secret is too short. Must be at least 32 characters. " +
            "Generate one with: node -e \"logger.info(require('crypto').randomBytes(48).toString('base64'))\""
        );
    }

    if (weakSecrets.has(config.secret.toLowerCase())) {
        throw new Error(
            "JWT secret is a known default/weak value. Please use a strong, randomly generated secret. " +
            "Generate one with: node -e \"logger.info(require('crypto').randomBytes(48).toString('base64'))\""
        );
    }

    jwtConfig = {
        ...jwtConfig,
        ...config
    };
}

/**
 * Generate an access token (short-lived, 1 hour by default)
 */
export function generateAccessToken(
    userId: string,
    roles: string[],
    aal: "aal1" | "aal2" = "aal1",
    customClaims?: Record<string, unknown>
): string {
    if (!jwtConfig.secret) {
        throw new Error("JWT secret not configured. Call configureJwt() first.");
    }

    const payload: Record<string, unknown> = {
        userId,
        roles,
        aal,
        ...customClaims
    };

    return jwt.sign(payload, jwtConfig.secret, {
        expiresIn: jwtConfig.accessExpiresIn as jwt.SignOptions["expiresIn"],
        algorithm: "HS256"
    });
}

/**
 * Get the expiration time of an access token in milliseconds from now
 */
export function getAccessTokenExpiryMs(): number {
    const duration = jwtConfig.accessExpiresIn || "1h";
    const match = duration.match(/^(\d+)([dhms])$/);

    if (!match) {
        // Default to 1 hour
        return 60 * 60 * 1000;
    }

    const value = parseInt(match[1], 10);
    const unit = match[2];

    switch (unit) {
        case "d": return value * 24 * 60 * 60 * 1000;
        case "h": return value * 60 * 60 * 1000;
        case "m": return value * 60 * 1000;
        case "s": return value * 1000;
        default: return 60 * 60 * 1000;
    }
}

/**
 * Get the expiration timestamp for an access token
 */
export function getAccessTokenExpiry(): number {
    return Date.now() + getAccessTokenExpiryMs();
}

/**
 * Verify and decode an access token
 */
export function verifyAccessToken(token: string): AccessTokenPayload | null {
    if (!jwtConfig.secret) {
        throw new Error("JWT secret not configured. Call configureJwt() first.");
    }

    try {
        const decoded = jwt.verify(token, jwtConfig.secret, { algorithms: ["HS256"] }) as { userId?: string; uid?: string; sub?: string; roles?: string[]; aal?: string };
        const id = decoded.userId || decoded.uid || decoded.sub;
        if (!id) {
            logger.error("[JWT] Verification failed: missing id in payload", { detail: decoded });
            return null;
        }

        const aal = (decoded.aal === "aal1" || decoded.aal === "aal2") ? decoded.aal : "aal1";

        return {
            userId: id,
            roles: decoded.roles || [],
            aal
        };
    } catch (error) {
        logger.error("[JWT] Verification failed", { error: error, detail: token.substring(0, 15) });
        return null;
    }
}

/**
 * Generate a random refresh token (long-lived, 30 days by default)
 */
export function generateRefreshToken(): string {
    return randomBytes(40).toString("hex");
}

/**
 * Hash a refresh token for database storage (don't store raw tokens)
 */
export function hashRefreshToken(token: string): string {
    return createHash("sha256").update(token).digest("hex");
}

/**
 * Calculate refresh token expiration date
 */
export function getRefreshTokenExpiry(): Date {
    const duration = jwtConfig.refreshExpiresIn || "30d";
    const match = duration.match(/^(\d+)([dhms])$/);

    if (!match) {
        // Default to 30 days
        return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    }

    const value = parseInt(match[1], 10);
    const unit = match[2];

    let ms: number;
    switch (unit) {
        case "d": ms = value * 24 * 60 * 60 * 1000; break;
        case "h": ms = value * 60 * 60 * 1000; break;
        case "m": ms = value * 60 * 1000; break;
        case "s": ms = value * 1000; break;
        default: ms = 30 * 24 * 60 * 60 * 1000;
    }

    return new Date(Date.now() + ms);
}

export interface DownloadTokenPayload {
    purpose: "file-read";
    path: string;
}

/**
 * Generate a short-lived download token scoped to a specific file path or prefix
 */
export function generateDownloadToken(
    path: string,
    expiresInSeconds: number = 300
): string {
    if (!jwtConfig.secret) {
        throw new Error("JWT secret not configured. Call configureJwt() first.");
    }

    const payload: DownloadTokenPayload = {
        purpose: "file-read",
        path
    };

    return jwt.sign(payload, jwtConfig.secret, {
        expiresIn: expiresInSeconds,
        algorithm: "HS256"
    });
}

/**
 * Verify and decode a download token
 */
export function verifyDownloadToken(token: string): DownloadTokenPayload | null {
    if (!jwtConfig.secret) {
        throw new Error("JWT secret not configured. Call configureJwt() first.");
    }

    try {
        const decoded = jwt.verify(token, jwtConfig.secret, { algorithms: ["HS256"] }) as Record<string, unknown> | undefined;
        if (decoded && decoded.purpose === "file-read" && typeof decoded.path === "string") {
            return {
                purpose: "file-read",
                path: decoded.path
            };
        }
        return null;
    } catch (error) {
        logger.error("[JWT] Download token verification failed", { error: error });
        return null;
    }
}

